// Контраст по WCAG 2.x для галереи дизайн-системы (DESIGN.md R15).
// Вход — строки из getComputedStyle: "rgb(r, g, b)" или "rgba(r, g, b, a)" (Chromium также
// отдаёт "color(srgb …)" для некоторых значений — такие считаем непрозрачным чёрным, чтобы
// проблема была видна, а не спрятана).

export type Rgba = [number, number, number, number];

export function parseRgb(s: string): Rgba {
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (!m) return [0, 0, 0, 1];
  const parts = m[1].split(/[\s,/]+/).filter(Boolean);
  const [r, g, b, a = '1'] = parts;
  return [Number(r), Number(g), Number(b), a.endsWith('%') ? Number(a.slice(0, -1)) / 100 : Number(a)];
}

function channel(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

export function luminance([r, g, b]: Rgba): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Компонует полупрозрачный цвет поверх непрозрачного фона (alpha-blend). */
export function composite(top: Rgba, under: Rgba): Rgba {
  const a = top[3];
  if (a >= 1) return [top[0], top[1], top[2], 1];
  return [
    top[0] * a + under[0] * (1 - a),
    top[1] * a + under[1] * (1 - a),
    top[2] * a + under[2] * (1 - a),
    1,
  ];
}

/** Контраст fg на bg; bg предварительно кладётся на base (например, полупрозрачный muted на canvas). */
export function contrastRatio(fg: string, bg: string, base?: string): number {
  let b = parseRgb(bg);
  if (base) b = composite(b, parseRgb(base));
  const f = composite(parseRgb(fg), b);
  const l1 = luminance(f);
  const l2 = luminance(b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

export type ContrastLevel = 'AA' | 'AA-large' | 'fail';

/** ≥ 4.5 — обычный текст; ≥ 3 — крупный текст и UI-элементы; иначе — провал. */
export function contrastLevel(ratio: number): ContrastLevel {
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA-large';
  return 'fail';
}

export function toHex([r, g, b, a]: Rgba): string {
  const h = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}${a < 1 ? h(a * 255) : ''}`.toUpperCase();
}
